import { useState, useEffect } from 'react';
import { Plus, Webhook, Edit, Trash2, RotateCcw, TestTube, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface WebhookConfig {
  id: string;
  name: string;
  url: string;
  events: string[];
  secret: string;
  is_active: boolean;
  created_at: string;
  last_triggered?: string;
  success_count: number;
  failure_count: number;
}

const availableEvents = [
  { value: 'guard.created', label: 'Guard Created' },
  { value: 'guard.updated', label: 'Guard Updated' },
  { value: 'guard.deleted', label: 'Guard Deleted' },
  { value: 'team.created', label: 'Team Created' },
  { value: 'team.updated', label: 'Team Updated' },
  { value: 'schedule.created', label: 'Schedule Created' },
  { value: 'schedule.updated', label: 'Schedule Updated' },
  { value: 'report.submitted', label: 'Report Submitted' },
  { value: 'mission.created', label: 'Mission Created' },
  { value: 'alert.triggered', label: 'Alert Triggered' },
];

export const WebhookManager = () => {
  const [webhooks, setWebhooks] = useState<WebhookConfig[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<WebhookConfig | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    events: [] as string[],
    secret: '',
    is_active: true
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isTesting, setIsTesting] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    try {
      // Create mock webhook data for demonstration
      const mockWebhooks: WebhookConfig[] = [
        {
          id: '1',
          name: 'Guard Notifications',
          url: 'https://api.example.com/guard-webhook',
          events: ['guard.created', 'guard.updated'],
          secret: 'webhook_secret_123',
          is_active: true,
          success_count: 25,
          failure_count: 2,
          created_at: new Date().toISOString(),
          last_triggered: new Date(Date.now() - 3600000).toISOString()
        }
      ];
      setWebhooks(mockWebhooks);
    } catch (error) {
      console.error('Error fetching webhooks:', error);
      toast({
        title: "Error",
        description: "Failed to fetch webhooks.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.url || formData.events.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    try {
      // For now, simulate successful webhook save
      toast({
        title: editingWebhook ? "Webhook Updated" : "Webhook Created",
        description: `Webhook has been ${editingWebhook ? 'updated' : 'created'} successfully.`
      });

      resetForm();
      setIsDialogOpen(false);
      fetchWebhooks();
    } catch (error) {
      console.error('Error saving webhook:', error);
      toast({
        title: "Error",
        description: "Failed to save webhook configuration.",
        variant: "destructive"
      });
    }
  };

  const deleteWebhook = async (id: string) => {
    try {
      toast({
        title: "Webhook Deleted",
        description: "Webhook has been deleted successfully."
      });

      fetchWebhooks();
    } catch (error) {
      console.error('Error deleting webhook:', error);
      toast({
        title: "Error",
        description: "Failed to delete webhook.",
        variant: "destructive"
      });
    }
  };

  const testWebhook = async (webhook: WebhookConfig) => {
    setIsTesting(webhook.id);
    try {
      // Simulate webhook test
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Test Successful",
        description: "Webhook test completed successfully. Response: 200"
      });
    } catch (error) {
      console.error('Error testing webhook:', error);
      toast({
        title: "Test Failed",
        description: "Failed to test webhook.",
        variant: "destructive"
      });
    } finally {
      setIsTesting(null);
    }
  };

  const regenerateSecret = () => {
    setFormData(prev => ({ ...prev, secret: generateSecret() }));
    toast({
      title: "Secret Regenerated",
      description: "A new webhook secret has been generated."
    });
  };

  const generateSecret = () => {
    return Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  };

  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      events: [],
      secret: '',
      is_active: true
    });
    setEditingWebhook(null);
  };

  const openEditDialog = (webhook: WebhookConfig) => {
    setFormData({
      name: webhook.name,
      url: webhook.url,
      events: webhook.events,
      secret: webhook.secret,
      is_active: webhook.is_active
    });
    setEditingWebhook(webhook);
    setIsDialogOpen(true);
  };

  const toggleEvent = (eventValue: string) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(eventValue)
        ? prev.events.filter(e => e !== eventValue)
        : [...prev.events, eventValue]
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Webhook className="w-6 h-6 text-primary" />
          <div>
            <h3 className="text-lg font-semibold">Webhook Management</h3>
            <p className="text-sm text-muted-foreground">
              Configure webhooks to receive real-time notifications
            </p>
          </div>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              Add Webhook
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingWebhook ? 'Edit Webhook' : 'Create New Webhook'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Webhook Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="My Webhook"
                  />
                </div>
                <div>
                  <Label htmlFor="url">Endpoint URL</Label>
                  <Input
                    id="url"
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://api.example.com/webhooks"
                  />
                </div>
              </div>

              <div>
                <Label>Events to Subscribe</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto border rounded p-3">
                  {availableEvents.map(event => (
                    <div key={event.value} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={event.value}
                        checked={formData.events.includes(event.value)}
                        onChange={() => toggleEvent(event.value)}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor={event.value} className="text-sm cursor-pointer">
                        {event.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="secret">Webhook Secret</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={regenerateSecret}
                  >
                    <RotateCcw className="w-3 h-3 mr-1" />
                    Generate
                  </Button>
                </div>
                <Input
                  id="secret"
                  value={formData.secret}
                  onChange={(e) => setFormData(prev => ({ ...prev, secret: e.target.value }))}
                  placeholder="Auto-generated secret"
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Used to verify webhook authenticity
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="active">Active</Label>
                  <p className="text-sm text-muted-foreground">
                    Enable this webhook to receive events
                  </p>
                </div>
                <Switch
                  id="active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
              </div>

              <Alert>
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  Make sure your endpoint can handle POST requests and verify webhook signatures using the secret.
                </AlertDescription>
              </Alert>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingWebhook ? 'Update Webhook' : 'Create Webhook'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {isLoading ? (
          <Card className="p-6 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent mx-auto"></div>
            <p className="text-sm text-muted-foreground mt-2">Loading webhooks...</p>
          </Card>
        ) : webhooks.length === 0 ? (
          <Card className="p-6 text-center">
            <Webhook className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold mb-2">No Webhooks Configured</h3>
            <p className="text-muted-foreground mb-4">
              Create your first webhook to start receiving real-time notifications.
            </p>
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Webhook
            </Button>
          </Card>
        ) : (
          webhooks.map(webhook => (
            <Card key={webhook.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-semibold">{webhook.name}</h4>
                    <Badge variant={webhook.is_active ? "default" : "secondary"}>
                      {webhook.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3 font-mono">
                    {webhook.url}
                  </p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {webhook.events.map(event => (
                      <Badge key={event} variant="outline" className="text-xs">
                        {availableEvents.find(e => e.value === event)?.label || event}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-4 text-sm text-muted-foreground">
                    <span>✓ {webhook.success_count} successful</span>
                    <span>✗ {webhook.failure_count} failed</span>
                    {webhook.last_triggered && (
                      <span>Last: {new Date(webhook.last_triggered).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => testWebhook(webhook)}
                    disabled={isTesting === webhook.id}
                  >
                    {isTesting === webhook.id ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-2 border-current border-t-transparent" />
                    ) : (
                      <TestTube className="w-3 h-3" />
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(webhook)}
                  >
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteWebhook(webhook.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};